// Picker INLINE de vistas solo-producto para el modo "Fotos de producto".
//
// A diferencia del `VistaPickerFullscreen` del Catálogo IA —que es un STEP
// propio, con pills de categoría, poses con-modelo y footer de confirmación—
// acá las vistas son pocas (≤6 por producto, ya filtradas por `model_view`),
// así que entran todas en una grilla dentro del panel de configuración. Sin
// "Ver más", sin overlay: el usuario ve el catálogo completo de una.
//
// Multi-select: cada vista elegida genera UNA imagen por cada producto
// aplicable y activo (un request por par, ver
// ADR-2026-08-13-fotos-producto-fan-out-por-vista y
// ADR-2026-08-30-fotos-producto-multi-producto-2).
//
// Con 2+ productos seleccionados, cada tile muestra en su esquina inferior
// derecha un stack de miniaturas (máx. 3 + "+N") de los productos ACTIVOS a
// los que la vista aplica — mismo idioma visual que el botón "Elegir de mis
// productos". Tocarlo abre un checklist (portal a body, se despliega desde
// el punto del tap como `useOpcionesMenu`) para activar/desactivar productos
// de esa vista. El último activo no se puede desactivar: para no generar
// nada de una vista, se desmarca la vista.

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, Sparkles } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import type { VistaProductoConjuntoOption } from '../vistasCatalogo';
import type { MockProductV2 } from '../mockData';

export interface VistaProductoPickerProps {
  /** Unión de vistas de los productos elegidos, con thumb resuelto y
   *  `productIds` (`vistasSoloProductoParaConjunto`). */
  vistas: VistaProductoConjuntoOption[];
  /** Ids (slugs) de las vistas seleccionadas. */
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Columnas de la grilla. Desktop usa 4; mobile 2. */
  columns?: 2 | 3 | 4;
  /** Id del contenedor de la grilla — target del scroll+shake cuando el
   *  usuario toca Generar sin ninguna vista elegida. El sheet monta las dos
   *  variantes a la vez, así que el id lo provee el caller con su prefijo. */
  gridId?: string;
  /** Productos seleccionados (para resolver thumb/nombre del badge y el
   *  checklist). El badge solo se muestra con 2 o más. */
  productos?: MockProductV2[];
  /** Productos desactivados por vista. Keyed por slug de vista. */
  excluidosPorVista?: Record<string, string[]>;
  /** Toggle de un producto para una vista. Sin handler no hay checklist. */
  onToggleProducto?: (vistaId: string, productId: string) => void;
  /**
   * Ancho MÍNIMO de cada tile, en px. Con esto la grilla se reparte por ancho
   * (`auto-fill`) en vez de por un número fijo de columnas, y `columns` se ignora.
   *
   * Lo usa la columna de vistas del rediseño, que puede medir 400px o 1000px según el
   * monitor: con 4 columnas fijas los tiles caían a ~93px y el nombre de la vista se
   * partía en tres renglones encima de la foto
   * (ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo). Es el mismo criterio
   * que ya usa el picker del Catálogo — ver
   * ADR-2026-09-04-las-vistas-se-reparten-por-ancho-y-no-por-numero.
   */
  anchoMinimoTile?: number;
}

/** Ancho del popover checklist. */
const MENU_WIDTH = 264;

export const VistaProductoPicker: React.FC<VistaProductoPickerProps> = ({
  vistas,
  selectedIds,
  onChange,
  columns = 3,
  anchoMinimoTile,
  gridId,
  productos = [],
  excluidosPorVista = {},
  onToggleProducto,
}) => {
  const toggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((v) => v !== id)
        : [...selectedIds, id],
    );
  };

  // Popover checklist de productos de UNA vista. La posición se calcula al
  // tocar el badge (mismo criterio que `useOpcionesMenu`: aparece desde el
  // punto del tap y se abre hacia arriba/izquierda si no entra).
  const [menuVistaId, setMenuVistaId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null,
  );
  const productosById = new Map(productos.map((p) => [p.id, p]));
  const showBadges = productos.length > 1 && !!onToggleProducto;

  const openMenu = (e: React.MouseEvent, vista: VistaProductoConjuntoOption) => {
    if (menuVistaId === vista.id) {
      setMenuVistaId(null);
      return;
    }
    const estHeight = vista.productIds.length * 48 + 44;
    let left = e.clientX - MENU_WIDTH;
    let top = e.clientY + 8;
    if (left < 12) left = 12;
    if (left + MENU_WIDTH + 12 > window.innerWidth) {
      left = window.innerWidth - MENU_WIDTH - 12;
    }
    if (top + estHeight + 12 > window.innerHeight) {
      top = e.clientY - estHeight - 8;
    }
    setMenuPos({ top: Math.max(12, top), left });
    setMenuVistaId(vista.id);
  };

  const menuVista = menuVistaId
    ? vistas.find((v) => v.id === menuVistaId)
    : null;
  const menu =
    menuVista && menuPos
      ? ReactDOM.createPortal(
          <>
            <div
              className="fixed inset-0 z-[300]"
              onClick={() => setMenuVistaId(null)}
            />
            <div
              className="fixed z-[301] bg-white rounded-2xl shadow-xl border border-neutral-200 p-2 flex flex-col gap-0.5 animate-mode-swap"
              style={{ top: menuPos.top, left: menuPos.left, width: MENU_WIDTH }}
            >
              <p className="px-3 pt-1.5 pb-1 text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                Productos de esta vista
              </p>
              {menuVista.productIds.map((pid) => {
                const producto = productosById.get(pid);
                if (!producto) return null;
                const excluidos = excluidosPorVista[menuVista.id] ?? [];
                const activo = !excluidos.includes(pid);
                const activos = menuVista.productIds.filter(
                  (id) => !excluidos.includes(id),
                );
                // El último activo no se puede desactivar (la vista elegida
                // siempre genera al menos una imagen).
                const esUltimoActivo = activo && activos.length <= 1;
                return (
                  <button
                    key={pid}
                    type="button"
                    disabled={esUltimoActivo}
                    onClick={() => onToggleProducto?.(menuVista.id, pid)}
                    className={`w-full inline-flex items-center gap-2.5 py-2 px-3 rounded-xl text-left transition-colors ${
                      esUltimoActivo
                        ? 'cursor-default'
                        : 'hover:bg-neutral-50'
                    }`}
                    title={
                      esUltimoActivo
                        ? 'La vista necesita al menos un producto — desmarcá la vista para no generarla.'
                        : undefined
                    }
                  >
                    <CachedImg
                      src={producto.imageUrl}
                      alt={producto.name}
                      className={`w-8 h-8 rounded-lg object-cover shrink-0 transition-opacity ${
                        activo ? '' : 'opacity-40'
                      }`}
                    />
                    <span
                      className={`flex-1 min-w-0 truncate text-[13.5px] font-medium transition-colors ${
                        activo ? 'text-brand-black/85' : 'text-[#3C3C43]/45'
                      }`}
                    >
                      {producto.name}
                    </span>
                    <span
                      className={`w-5 h-5 shrink-0 inline-flex items-center justify-center rounded-md transition-colors ${
                        activo
                          ? `bg-brand-primary text-white ${
                              esUltimoActivo ? 'opacity-50' : ''
                            }`
                          : 'border border-neutral-300 bg-white'
                      }`}
                    >
                      {activo && <Check className="w-3 h-3" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </>,
          document.body,
        )
      : null;

  if (vistas.length === 0) {
    // Defensivo: `isVistaCompatibleConModelView` devuelve TODAS las vistas
    // cuando el producto no tiene `model_view`, así que llegar acá implicaría
    // un catálogo vacío. Mejor un texto que una grilla fantasma.
    return (
      <p className="text-[13px] text-[#3C3C43]/60 leading-relaxed">
        No hay vistas disponibles para este producto.
      </p>
    );
  }

  return (
    <div
      id={gridId}
      style={
        anchoMinimoTile
          ? {
              gridTemplateColumns: `repeat(auto-fill, minmax(${anchoMinimoTile}px, 1fr))`,
            }
          : undefined
      }
      className={`grid gap-2.5 ${
        anchoMinimoTile
          ? ''
          : columns === 4
            ? 'grid-cols-4'
            : columns === 3
              ? 'grid-cols-3'
              : 'grid-cols-2'
      }`}
    >
      {vistas.map((vista) => {
        const selected = selectedIds.includes(vista.id);
        const excluidos = excluidosPorVista[vista.id] ?? [];
        // El stack muestra los productos ACTIVOS de la vista (lo que se va a
        // generar), no todos los aplicables.
        const productosActivos = showBadges
          ? vista.productIds
              .filter((id) => !excluidos.includes(id))
              .map((id) => productosById.get(id))
              .filter((p): p is MockProductV2 => !!p)
          : [];
        const stackThumbs = productosActivos.slice(0, 3);
        const stackExtra = productosActivos.length - stackThumbs.length;
        return (
          // El badge es un botón HERMANO del tile (no anidado: button dentro
          // de button es HTML inválido), superpuesto vía el wrapper relative.
          <div key={vista.id} className="relative">
            <button
              type="button"
              onClick={() => toggle(vista.id)}
              aria-pressed={selected}
              className={`relative w-full aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 active:scale-[0.98] transition-all ${
                selected
                  ? 'ring-2 ring-brand-primary'
                  : 'ring-1 ring-black/[0.06]'
              }`}
            >
              {vista.thumbUrl ? (
                <img
                  src={vista.thumbUrl}
                  alt={vista.nombre}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                // Vistas sin foto de ejemplo (hoy solo "Automática"): ícono +
                // nombre centrados en vez del placeholder "sin preview", que
                // dejaba una card muerta al lado de las que sí tienen foto.
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-2 text-center">
                  <Sparkles className="w-5 h-5 text-neutral-400" />
                  <span className="text-[12px] font-medium text-brand-black/80 leading-tight">
                    {vista.nombre}
                  </span>
                </div>
              )}
              {/* El gradiente + nombre en blanco solo tienen sentido sobre la
                  foto; sin thumb taparían el texto oscuro de arriba. */}
              {vista.thumbUrl && (
                <>
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
                  <p
                    className={`absolute bottom-2 left-2 text-left text-white text-[12.5px] font-medium leading-tight drop-shadow ${
                      stackThumbs.length > 0 ? 'right-16' : 'right-2'
                    }`}
                  >
                    {vista.nombre}
                  </p>
                </>
              )}
              {selected && (
                <span className="absolute top-2 right-2 w-6 h-6 inline-flex items-center justify-center rounded-full bg-brand-primary text-white">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
              )}
            </button>
            {stackThumbs.length > 0 && (
              <button
                type="button"
                onClick={(e) => openMenu(e, vista)}
                aria-label="Elegir productos de esta vista"
                aria-expanded={menuVistaId === vista.id}
                className="absolute bottom-4 right-1.5 z-10 p-0.5 rounded-lg active:scale-[0.95] transition-transform"
              >
                <span className="flex -space-x-2">
                  {stackThumbs.map((p, i) => (
                    <span
                      key={p.id}
                      className="relative w-6 h-6 rounded-md overflow-hidden bg-neutral-100 ring-2 ring-white shadow-sm"
                      style={{ zIndex: stackThumbs.length + 1 - i }}
                    >
                      <CachedImg
                        src={p.imageUrl}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </span>
                  ))}
                  {stackExtra > 0 && (
                    <span className="relative z-0 w-6 h-6 rounded-md bg-white ring-2 ring-white shadow-sm inline-flex items-center justify-center text-[10px] font-semibold text-brand-black/70">
                      +{stackExtra}
                    </span>
                  )}
                </span>
              </button>
            )}
          </div>
        );
      })}
      {menu}
    </div>
  );
};
